import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { ThemeContext } from "../../App";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Grid,
    Chip,
    Avatar,
    useTheme,
    Slide,
    Grow,
    Zoom,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TimerIcon from "@mui/icons-material/Timer";
import StarsIcon from "@mui/icons-material/Stars";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import styles from "./Leaderboard.module.css";

type LeaderboardMode =
    | "leaderboardSegueLinha"
    | "leaderboardVelocidade"
    | "leaderboardFinal"
    | "sponsors";

type RawTeamResult = {
    id: string;
    name: string;
    timestampStart?: number;
    timestampEnd?: number;
    prova?: string;
    penalties?: PenaltyInfo[];
    totalPenalty?: number;
    adjustedTime?: number;
};

type PenaltyInfo = {
    id?: number;
    provaId?: number;
    nome?: string;
    value?: number;
};

type Team = {
    id: string;
    name: string;
};

type ProvaStatus = {
    id?: number;
    teamId?: string;
    provaType?: "segueLinha" | "velocidade" | "undefined";
    enabled?: boolean;
    started?: boolean;
    startedTimeStamp?: number;
    ended?: boolean;
    endedTimeStamp?: number;
};

type ProcessedTeam = {
    id: string;
    name: string;
    time?: number;
    baseTime?: number; // Add this field to store the original time for reference
    points?: number;
    penaltyPoints?: number; // Add this field to track penalty impact on points
    position: number;
    progress: number;
    status: "not-started" | "in-progress" | "completed";
    isActive: boolean;
    penalties?: PenaltyInfo[];
    totalPenalty?: number;
};

type Sponsor = {
    id: string;
    name: string;
    logo: string;
    weight?: number; // Add weight property from API
};

const Leaderboard = () => {
    const { isDarkMode } = useContext(ThemeContext);
    const theme = useTheme();
    const [mode, setMode] = useState<LeaderboardMode>("leaderboardSegueLinha");
    const [loading, setLoading] = useState(true);
    const [allTeams, setAllTeams] = useState<Team[]>([]);
    const [rawResults, setRawResults] = useState<RawTeamResult[]>([]);
    const [provaStatus, setProvaStatus] = useState<ProvaStatus | null>(null);
    const [sponsors, setSponsors] = useState<Sponsor[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [visibleSponsors, setVisibleSponsors] = useState<Sponsor[]>([]);
    const [transition, setTransition] = useState(true);

    // Ref for scrolling to active team
    const activeTeamRef = useRef<HTMLDivElement>(null);

    // Fetch all teams once at component mount
    useEffect(() => {
        const fetchAllTeams = async () => {
            try {
                const response = await axios.get("/team/");
                setAllTeams(response.data || []);
            } catch (err) {
                console.error("Failed to fetch teams:", err);
            }
        };

        fetchAllTeams();
    }, []);

    // Process raw data from API into usable format
    const processedResults = useMemo(() => {
        // Create a map to store team results indexed by ID
        const teamResultsMap: Record<string, ProcessedTeam> = {};

        // First, initialize with all teams (to include those who haven't participated)
        allTeams.forEach((team) => {
            teamResultsMap[team.id] = {
                id: team.id,
                name: team.name,
                position: 0, // Will be assigned later for teams with results
                progress: 0,
                status: "not-started",
                isActive: false,
            };
        });

        if (mode === "leaderboardFinal") {
            // Group by team and calculate total time
            rawResults.forEach((result) => {
                if (!result.id || !result.name) return;

                // Calculate base time without penalties
                const baseTimeElapsed =
                    result.timestampEnd && result.timestampStart
                        ? (result.timestampEnd - result.timestampStart) / 1000
                        : 0;

                // Calculate penalty time
                const penaltyTime = result.totalPenalty || 0;

                // Final time calculation (base time plus penalty time)
                const finalTime = baseTimeElapsed + penaltyTime;

                // Store penalties info
                const penalties = result.penalties || [];
                const totalPenalty = result.totalPenalty || 0;

                if (!teamResultsMap[result.id]) {
                    teamResultsMap[result.id] = {
                        id: result.id,
                        name: result.name,
                        time: finalTime,
                        baseTime: baseTimeElapsed,
                        position: 0,
                        progress: 0,
                        status: "completed",
                        isActive: false,
                        penalties,
                        totalPenalty: penaltyTime,
                    };
                } else {
                    const currentTime = teamResultsMap[result.id].time || 0;
                    const currentBaseTime =
                        teamResultsMap[result.id].baseTime || 0;

                    teamResultsMap[result.id].time = currentTime + finalTime;
                    teamResultsMap[result.id].baseTime =
                        currentBaseTime + baseTimeElapsed;
                    teamResultsMap[result.id].status = "completed";

                    // We might have penalties from different provas, so combine them
                    const currentPenalties =
                        teamResultsMap[result.id].penalties || [];
                    teamResultsMap[result.id].penalties = [
                        ...currentPenalties,
                        ...penalties,
                    ];
                    teamResultsMap[result.id].totalPenalty =
                        (teamResultsMap[result.id].totalPenalty || 0) +
                        totalPenalty;
                }
            });

            // Get teams with results for ranking
            const teamsWithResults = Object.values(teamResultsMap).filter(
                (team) => team.time !== undefined && team.time > 0
            );

            // Sort by time (ascending - faster/lower times are better)
            teamsWithResults.sort((a, b) => {
                // Ensure total time includes penalties
                const totalTimeA = (a.time ?? 0) + (a.totalPenalty ?? 0) || 999;
                const totalTimeB = (b.time ?? 0) + (b.totalPenalty ?? 0) || 999;
                return totalTimeA - totalTimeB;
            });

            // Assign positions
            teamsWithResults.forEach((team, index) => {
                team.position = index + 1;
            });

            // Calculate progress (best time gets 100%)
            const bestTime =
                teamsWithResults.length > 0 && teamsWithResults[0].time
                    ? teamsWithResults[0].time
                    : 1;

            teamsWithResults.forEach((team) => {
                if (team.time) {
                    team.progress = Math.min(
                        100,
                        Math.round((bestTime / team.time) * 100)
                    );
                }
            });

            // Check for active team
            if (provaStatus?.teamId) {
                const activeTeam = teamResultsMap[provaStatus.teamId];
                if (activeTeam) {
                    activeTeam.isActive = true;
                    activeTeam.status =
                        provaStatus.started && !provaStatus.ended
                            ? "in-progress"
                            : activeTeam.status;
                }
            }

            // Get teams without results
            const teamsWithoutResults = Object.values(teamResultsMap).filter(
                (team) => team.time === undefined || team.time === 0
            );

            // Return all teams, with ranked teams first
            return [...teamsWithResults, ...teamsWithoutResults];
        } else {
            // For single prova (segueLinha or velocidade)
            const currentProvaType =
                mode === "leaderboardSegueLinha" ? "segueLinha" : "velocidade";

            // Process teams with results
            rawResults.forEach((result) => {
                if (!result.id || !result.name) return;

                // Check if result is for current prova type (if specified)
                if (result.prova && result.prova !== currentProvaType) {
                    return;
                }

                // Calculate elapsed time (base time)
                const baseTime =
                    result.timestampEnd && result.timestampStart
                        ? result.timestampEnd - result.timestampStart
                        : 0;

                // Get penalty time in milliseconds (make sure it's not in seconds)
                const penaltyTime = result.totalPenalty || 0;

                const finalTime = baseTime / 1000;

                // Store penalties info
                const penalties = result.penalties || [];
                const totalPenalty = penaltyTime;

                if (teamResultsMap[result.id]) {
                    teamResultsMap[result.id] = {
                        ...teamResultsMap[result.id],
                        time: finalTime + totalPenalty, // Use the new calculation with penalties included
                        baseTime: baseTime / 1000, // Store original time for reference
                        penalties: penalties,
                        totalPenalty: totalPenalty,
                        status: "completed",
                    };
                } else {
                    teamResultsMap[result.id] = {
                        id: result.id,
                        name: result.name,
                        time: finalTime, // Use the new calculation with penalties included
                        baseTime: baseTime / 1000, // Store original time for reference
                        penalties: penalties,
                        totalPenalty: totalPenalty,
                        position: 0,
                        progress: 0,
                        status: "completed",
                        isActive: false,
                    };
                }
            });

            // Get teams with results for ranking
            const teamsWithResults = Object.values(teamResultsMap).filter(
                (team) => team.time !== undefined
            );

            // Sort by time
            teamsWithResults.sort((a, b) => {
                // Ensure total time includes penalties
                const totalTimeA = (a.time ?? 0) + (a.totalPenalty ?? 0) || 999;
                const totalTimeB = (b.time ?? 0) + (b.totalPenalty ?? 0) || 999;
                return totalTimeA - totalTimeB;
            });

            // Assign positions
            teamsWithResults.forEach((team, index) => {
                team.position = index + 1;
            });

            // Calculate progress
            const bestTime =
                teamsWithResults.length > 0 && teamsWithResults[0].time
                    ? teamsWithResults[0].time
                    : 1;

            teamsWithResults.forEach((team) => {
                if (team.time) {
                    team.progress = Math.min(
                        100,
                        Math.round((bestTime / team.time) * 100)
                    );
                }
            });

            // Check for active team in current prova
            if (
                provaStatus?.teamId &&
                provaStatus.provaType === currentProvaType
            ) {
                const activeTeam = teamResultsMap[provaStatus.teamId];
                if (activeTeam) {
                    activeTeam.isActive = true;
                    activeTeam.status =
                        provaStatus.started && !provaStatus.ended
                            ? "in-progress"
                            : activeTeam.status;
                }
            }

            // Get teams without results
            const otherTeams = Object.values(teamResultsMap).filter(
                (team) => team.time === undefined
            );

            // Return all teams, with ranked teams first, then teams without results
            return [...teamsWithResults, ...otherTeams];
        }
    }, [rawResults, allTeams, provaStatus, mode]);

    // Fetch the current leaderboard mode
    // Update the useEffect for mode fetching to only transition when mode changes
    useEffect(() => {
        const fetchMode = async () => {
            try {
                const response = await axios.get("/leaderboard/mode");
                const newMode = response.data.mode;

                // Only trigger transition if the mode is actually changing
                if (newMode !== mode) {
                    setTransition(false); // Start transition out

                    // Small delay before changing mode to allow transition
                    setTimeout(() => {
                        setMode(newMode);

                        // Allow time for data to load before transitioning back in
                        setTimeout(() => setTransition(true), 100);
                    }, 300);
                }
            } catch (err) {
                console.error("Failed to fetch leaderboard mode:", err);
                setError("Failed to load leaderboard configuration");
            }
        };

        fetchMode();

        // Poll for mode changes every 5 seconds
        const intervalId = setInterval(fetchMode, 5000);
        return () => clearInterval(intervalId);
    }, [mode]); // Add mode as a dependency to access its current value

    // Fetch the current prova status
    useEffect(() => {
        const fetchProvaStatus = async () => {
            try {
                const response = await axios.get("/provas/status");
                setProvaStatus(response.data);
            } catch (err) {
                console.error("Failed to fetch prova status:", err);
            }
        };

        fetchProvaStatus();

        // Poll for status changes every 2 seconds
        const intervalId = setInterval(fetchProvaStatus, 2000);
        return () => clearInterval(intervalId);
    }, []);

    // Fetch the appropriate data based on the mode
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                if (mode === "sponsors") {
                    // Fetch sponsors data
                    const response = await axios.get("/leaderboard/sponsors");
                    setSponsors(response.data.sponsors || []);
                } else {
                    // Fetch appropriate leaderboard data
                    const endpoint =
                        mode === "leaderboardFinal"
                            ? "/leaderboard/final"
                            : `/leaderboard/${
                                  mode === "leaderboardSegueLinha"
                                      ? "segue-linha"
                                      : "velocidade"
                              }`;

                    const response = await axios.get(endpoint);
                    setRawResults(response.data.results || []);
                }
                setError(null);
            } catch (err) {
                console.error("Failed to fetch leaderboard data:", err);
                setError("Failed to load leaderboard data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        // Poll for data updates every 5 seconds
        const intervalId = setInterval(fetchData, 5000);
        return () => clearInterval(intervalId);
    }, [mode]);

    // Scroll to active team when one is detected
    useEffect(() => {
        if (activeTeamRef.current) {
            activeTeamRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [processedResults]);

    // Handle sponsor rotation for sponsor mode
    useEffect(() => {
        if (mode === "sponsors" && sponsors.length > 0) {
            // Function to get random sponsors with weighted selection
            const getWeightedRandomSponsors = () => {
                // Calculate how many logos we need to fill the screen
                const screenWidth = window.innerWidth;
                const screenHeight = window.innerHeight;

                // Reduce logo size to fit more on screen
                const logoWidth = 200; // Width in pixels (reduced from 250)
                const logoHeight = 160; // Height in pixels (reduced from 180)
                const gapSize = 16; // Gap between logos (reduced from 20)

                // Calculate grid dimensions with some extra to ensure good coverage
                const cols =
                    Math.floor(screenWidth / (logoWidth + gapSize)) + 1;
                const rows =
                    Math.floor(screenHeight / (logoHeight + gapSize)) + 1;

                // Calculate total slots needed (add extra to ensure full coverage)
                const totalSlots = cols * rows + 4;

                // Create a weighted selection based on the weight property from the API
                const weightedSponsors: Sponsor[] = [];
                sponsors.forEach((sponsor) => {
                    // Use the weight property from the API (default to 1 if not defined)
                    const weight = sponsor.weight || 1;

                    // Add sponsor to the array multiple times based on its weight
                    for (let i = 0; i < weight; i++) {
                        weightedSponsors.push(sponsor);
                    }
                });

                // Fill the grid with random selections
                const selected: Sponsor[] = [];
                for (let i = 0; i < totalSlots; i++) {
                    const randomIndex = Math.floor(
                        Math.random() * weightedSponsors.length
                    );
                    selected.push(weightedSponsors[randomIndex]);
                }

                return selected;
            };

            // Set initial random selection
            setVisibleSponsors(getWeightedRandomSponsors());

            // Reshuffle every 8 seconds
            const intervalId = setInterval(() => {
                setTransition(false);

                // Wait for transition out before updating
                setTimeout(() => {
                    setVisibleSponsors(getWeightedRandomSponsors());
                    setTransition(true);
                }, 400);
            }, 8000);

            // Update on window resize
            const handleResize = () => {
                setVisibleSponsors(getWeightedRandomSponsors());
            };
            window.addEventListener("resize", handleResize);

            return () => {
                clearInterval(intervalId);
                window.removeEventListener("resize", handleResize);
            };
        }
    }, [mode, sponsors]);

    // Format time display (convert seconds to mm:ss.ms format)
    const formatTime = (time?: number) => {
        if (!time && time !== 0) return "-";

        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        const milliseconds = Math.floor((time % 1) * 100);

        return `${minutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}.${milliseconds.toString().padStart(2, "0")}`;
    };

    // Render the title based on the mode
    const renderTitle = () => {
        switch (mode) {
            case "leaderboardSegueLinha":
                return (
                    <Box display="flex" alignItems="center">
                        <SportsScoreIcon sx={{ mr: 1.5 }} />
                        Segue Linha - Classificação
                    </Box>
                );
            case "leaderboardVelocidade":
                return (
                    <Box display="flex" alignItems="center">
                        <TimerIcon sx={{ mr: 1.5 }} />
                        Velocidade - Classificação
                    </Box>
                );
            case "leaderboardFinal":
                return (
                    <Box display="flex" alignItems="center">
                        <StarsIcon sx={{ mr: 1.5 }} />
                        Classificação Final
                    </Box>
                );
            case "sponsors":
                return "Patrocinadores";
            default:
                return "Leaderboard";
        }
    };

    // Get medal color for top positions
    const getMedalColor = (position: number) => {
        switch (position) {
            case 1:
                return theme.palette.mode === "dark" ? "#FFD700" : "#FFD700"; // Gold
            case 2:
                return theme.palette.mode === "dark" ? "#C0C0C0" : "#C0C0C0"; // Silver
            case 3:
                return theme.palette.mode === "dark" ? "#CD7F32" : "#CD7F32"; // Bronze
            default:
                return "transparent";
        }
    };

    // Get color for team status
    const getStatusColor = (status: string, isActive: boolean) => {
        if (isActive) return "#ff9800"; // Orange for active

        switch (status) {
            case "completed":
                return "#4caf50"; // Green
            case "in-progress":
                return "#ff9800"; // Orange
            default:
                return "#9e9e9e"; // Grey
        }
    };

    // Get icon for team status
    const getStatusIcon = (status: string, isActive: boolean) => {
        if (isActive) return <DirectionsRunIcon fontSize="small" />;

        switch (status) {
            case "completed":
                return <CheckCircleIcon fontSize="small" />;
            case "in-progress":
                return <DirectionsRunIcon fontSize="small" />;
            default:
                return <HourglassEmptyIcon fontSize="small" />;
        }
    };

    // Render leaderboard content
    const renderLeaderboardContent = () => {
        if (loading && !processedResults.length) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    height="200px">
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
                        Carregando resultados...
                    </Typography>
                </Box>
            );
        }

        if (error && !processedResults.length && !sponsors.length) {
            return (
                <Box textAlign="center" p={4}>
                    <Typography color="error" variant="h6">
                        {error}
                    </Typography>
                </Box>
            );
        }

        if (mode === "sponsors") {
            return (
                <Grid
                    container
                    spacing={2.5}
                    sx={{
                        width: "100%",
                        height: "100vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "10px",
                        overflow: "hidden",
                    }}>
                    {visibleSponsors.map((sponsor, index) => (
                        <Grid
                            key={`${sponsor.id}-${index}`}
                            sx={{
                                textAlign: "center",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: { xs: "160px", sm: "180px" },
                            }}>
                            <Zoom
                                in={transition}
                                style={{
                                    transitionDelay: `${30 * (index % 10)}ms`,
                                }}
                                timeout={400}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "100%",
                                        width: "100%",
                                        transition: "transform 0.3s ease",
                                        "&:hover": {
                                            transform: "scale(1.05)",
                                        },
                                    }}>
                                    <Box
                                        component="img"
                                        src={sponsor.logo}
                                        alt={sponsor.name}
                                        onError={(
                                            e: React.SyntheticEvent<HTMLImageElement>
                                        ) => {
                                            e.currentTarget.src =
                                                "/sponsor-placeholder.png";
                                        }}
                                        sx={{
                                            maxWidth: "250px",
                                            maxHeight: "250px",
                                            objectFit: "contain",
                                            filter: isDarkMode
                                                ? "drop-shadow(0 4px 6px rgba(255,255,255,0.1))"
                                                : "drop-shadow(0 4px 6px rgba(0,0,0,0.1))",
                                        }}
                                    />
                                </Box>
                            </Zoom>
                        </Grid>
                    ))}
                </Grid>
            );
        } else {
            return (
                <Box
                    className={styles.leaderboardContent}
                    sx={{
                        msOverflowStyle: "none",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                        overflow: "auto",
                    }}>
                    {processedResults.map((team, index) => {
                        const isActive = team.isActive;

                        // Calculate staggered delay based on position
                        const transitionDelay = `${80 * Math.min(index, 10)}ms`;

                        return (
                            <Grow
                                key={team.id}
                                in={transition}
                                style={{
                                    transformOrigin: "0 0 0",
                                    transitionDelay,
                                }}
                                timeout={400}>
                                <Paper
                                    ref={isActive ? activeTeamRef : null}
                                    elevation={isActive ? 6 : 2}
                                    className={`${styles.teamCard} ${
                                        isActive ? styles.activeTeam : ""
                                    }`}
                                    sx={{
                                        borderLeft: `4px solid ${
                                            team.position > 0
                                                ? getMedalColor(team.position)
                                                : getStatusColor(
                                                      team.status,
                                                      isActive
                                                  )
                                        }`,
                                        backgroundColor: isDarkMode
                                            ? isActive
                                                ? "rgba(255, 152, 0, 0.15)"
                                                : team.position <= 3 &&
                                                  team.position > 0
                                                ? "rgba(50, 50, 50, 0.95)"
                                                : "rgba(40, 40, 40, 0.9)"
                                            : isActive
                                            ? "rgba(255, 152, 0, 0.05)"
                                            : team.position <= 3 &&
                                              team.position > 0
                                            ? "rgba(255, 255, 255, 0.95)"
                                            : "rgba(248, 248, 248, 0.9)",
                                        transform: isActive
                                            ? "scale(1.02)"
                                            : "none",
                                        transition: "all 0.3s ease",
                                    }}>
                                    <Box className={styles.teamCardContent}>
                                        <Box
                                            className={
                                                styles.positionContainer
                                            }>
                                            <Avatar
                                                className={
                                                    styles.positionAvatar
                                                }
                                                sx={{
                                                    bgcolor:
                                                        team.position > 0
                                                            ? getMedalColor(
                                                                  team.position
                                                              )
                                                            : getStatusColor(
                                                                  team.status,
                                                                  isActive
                                                              ),
                                                    color:
                                                        team.position <= 3 &&
                                                        team.position > 0
                                                            ? "#000"
                                                            : isDarkMode
                                                            ? "#fff"
                                                            : "#666",
                                                }}>
                                                {team.position > 0 ? (
                                                    team.position === 1 ? (
                                                        <EmojiEventsIcon fontSize="small" />
                                                    ) : (
                                                        team.position
                                                    )
                                                ) : (
                                                    getStatusIcon(
                                                        team.status,
                                                        isActive
                                                    )
                                                )}
                                            </Avatar>
                                        </Box>

                                        <Box className={styles.teamInfo}>
                                            <Typography
                                                variant="body1"
                                                fontWeight={500}
                                                className={styles.teamName}>
                                                {team.name}
                                                {isActive && (
                                                    <Chip
                                                        size="small"
                                                        label="EM PROVA"
                                                        color="warning"
                                                        sx={{
                                                            ml: 1,
                                                            height: 20,
                                                            fontSize: "0.7rem",
                                                        }}
                                                    />
                                                )}
                                            </Typography>

                                            {mode === "leaderboardFinal" ? (
                                                <></>
                                            ) : (
                                                <>
                                                    {(team.penalties ?? [])
                                                        .length > 0 && (
                                                        <Box
                                                            sx={{
                                                                mt: 0.5,
                                                                display: "flex",
                                                                flexWrap:
                                                                    "wrap",
                                                                gap: 0.5,
                                                            }}>
                                                            {(
                                                                team.penalties ??
                                                                []
                                                            ).map(
                                                                (
                                                                    penalty,
                                                                    idx
                                                                ) => (
                                                                    <Chip
                                                                        key={
                                                                            idx
                                                                        }
                                                                        size="small"
                                                                        label={`${
                                                                            penalty.nome
                                                                        }: +${
                                                                            penalty.value ||
                                                                            0
                                                                        }s`}
                                                                        color="error"
                                                                        variant="outlined"
                                                                        sx={{
                                                                            height: 18,
                                                                            fontSize:
                                                                                "0.65rem",
                                                                        }}
                                                                    />
                                                                )
                                                            )}
                                                        </Box>
                                                    )}
                                                </>
                                            )}

                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    mt: 1.5,
                                                    mb: 0.5,
                                                }}>
                                                {/* Progress bar remains unchanged */}
                                            </Box>
                                        </Box>

                                        <Box className={styles.resultContainer}>
                                            {team.status === "not-started" ? (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary">
                                                    Aguardando
                                                </Typography>
                                            ) : team.status ===
                                              "in-progress" ? (
                                                <Typography
                                                    variant="body2"
                                                    color="warning.main"
                                                    className={
                                                        styles.inProgressText
                                                    }>
                                                    Em prova
                                                </Typography>
                                            ) : (
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        className={
                                                            styles.resultValue
                                                        }>
                                                        {mode ===
                                                        "leaderboardFinal" ? (
                                                            <Box
                                                                component="span"
                                                                className={
                                                                    styles.points
                                                                }>
                                                                {formatTime(
                                                                    team.time ||
                                                                        0
                                                                )}{" "}
                                                            </Box>
                                                        ) : (
                                                            <>
                                                                <Box
                                                                    component="span"
                                                                    className={
                                                                        styles.time
                                                                    }>
                                                                    {formatTime(
                                                                        team.time ??
                                                                            0
                                                                    )}{" "}
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        fontSize:
                                                                            "0.75rem",
                                                                        color: "text.secondary",
                                                                        mt: 0.5,
                                                                        display:
                                                                            "flex",
                                                                        flexDirection:
                                                                            "column",
                                                                        gap: "2px",
                                                                    }}>
                                                                    <span>
                                                                        Base:{" "}
                                                                        {formatTime(
                                                                            team.baseTime ||
                                                                                0
                                                                        )}
                                                                    </span>
                                                                    <span>
                                                                        Penalidades:{" "}
                                                                        {formatTime(
                                                                            team.totalPenalty
                                                                                ? team.totalPenalty
                                                                                : 0
                                                                        )}
                                                                    </span>
                                                                </Box>
                                                            </>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grow>
                        );
                    })}
                </Box>
            );
        }
    };

    return mode === "sponsors" ? (
        // For sponsors mode, render just the sponsors grid without the container
        <Slide
            direction="up"
            in={transition}
            mountOnEnter
            unmountOnExit
            timeout={400}>
            <Box className={styles.leaderboardContent}>
                {renderLeaderboardContent()}
            </Box>
        </Slide>
    ) : (
        // For all other modes, keep the existing container
        <Paper
            elevation={4}
            className={`${styles.leaderboardContainer} ${
                isDarkMode ? styles.dark : styles.light
            }`}
            sx={{
                overflow: "hidden",
                borderRadius: "16px",
                backgroundImage: isDarkMode
                    ? "linear-gradient(135deg, rgba(30,30,30,0.95), rgba(20,20,20,0.97))"
                    : "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(245,245,245,0.97))",
                boxShadow: isDarkMode
                    ? "0 10px 30px rgba(0,0,0,0.3)"
                    : "0 10px 30px rgba(0,0,0,0.1)",
            }}>
            <Zoom in={transition} timeout={400}>
                <Box
                    className={styles.leaderboardHeader}
                    sx={{
                        backgroundImage:
                            "linear-gradient(135deg, #0077b6, #023e8a)",
                        color: "white",
                        padding: "1.25rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        className={styles.leaderboardTitle}>
                        {renderTitle()}
                    </Typography>
                </Box>
            </Zoom>
            <Slide
                direction="up"
                in={transition}
                mountOnEnter
                unmountOnExit
                timeout={400}>
                <Box
                    className={styles.leaderboardContent}
                    sx={{
                        msOverflowStyle: "none",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                        overflow: "auto",
                    }}>
                    {renderLeaderboardContent()}
                </Box>
            </Slide>
        </Paper>
    );
};

export default Leaderboard;
